import React from "react";
import { Activity, Clock, Target, TriangleAlert } from "lucide-react";
import { MilestoneIcon } from "../../views/dashboard/icons/icons";
import { TimelineTaskIcons } from "../../helpers/utils";
import styles from "./resourceInsights.module.scss";
import { formatDateRange, formatHours, pluralize } from "./cardFormat";
import AllocationDonut from "./AllocationDonut";
import PerformanceSection from "./PerformanceSection";
import BudgetSection from "./BudgetSection";

/**
 * Short, data-derived callouts that point at what needs attention. The ones about tasks open the tasks they are
 * about; the one about the hours has nothing to open.
 */
function buildHighlights({ period, counts, showHours }) {
  const highlights = [];

  if (showHours && period && period.utilizationPercent > 100) {
    highlights.push({ tone: "warning", text: `Over-allocated: ${Math.round(period.utilizationPercent)}% of the assigned hours` });
  }
  if (counts?.overdue > 0) {
    highlights.push({ tone: "warning", text: pluralize(counts.overdue, "overdue task"), count: "overdue" });
  }
  if (counts?.critical > 0) {
    highlights.push({ tone: "warning", text: `${pluralize(counts.critical, "critical task")} in progress`, count: "critical" });
  }
  if (counts?.openRisks > 0) {
    highlights.push({ tone: "info", text: `${pluralize(counts.openRisks, "open risk")} on assigned tasks`, count: "openRisks" });
  }
  if (counts && highlights.length === 0) {
    highlights.push({ tone: "ok", text: "No overdue or critical tasks" });
  }

  return highlights;
}

const COMPLETED_STATUS = 3;

/**
 * The counts of tasks, each in the colour of what it counts. Milestones carry the diamond of a milestone and
 * completed tasks the icon a completed task has on the timeline.
 */
const STAT_TILES = [
  { key: "active", label: "Active", icon: () => <Activity size={13} /> },
  { key: "overdue", label: "Overdue", icon: () => <Clock size={13} /> },
  { key: "critical", label: "Critical", icon: () => <Target size={13} /> },
  { key: "milestones", label: "Milestones", icon: () => <MilestoneIcon size={13} /> },
  { key: "openRisks", label: "Open risks", icon: () => <TriangleAlert size={13} /> },
  { key: "completed", label: "Completed", icon: () => TimelineTaskIcons[COMPLETED_STATUS]() },
];

/**
 * The summary of a resource: how loaded they are in the period, how many tasks there are of each kind and where their
 * hours go. A count, a callout and a project open the tasks they stand for.
 *
 * @param performance  the state of the performance indices, as `useResourcePerformance` returns it; they are left out without it
 * @param budget  the state of the budget, as `useResourceBudget` returns it; it is left out without it
 * @param onOpenTasks  `(count, projectId?)`, asks for the list of the tasks behind a count, within a project if given
 */
export default function OverviewTab({ card, performance, budget, showHours, canOpenTasks, onOpenTasks }) {
  const { period, counts, maxWeeklyHours, isPartialView } = card;
  const highlights = buildHighlights({ period, counts, showHours });
  const utilization = period?.utilizationPercent ?? 0;
  const isOver = utilization > 100;

  return (
    <div className={styles.tabContent}>
      {highlights.length > 0 && (
        <div className={styles.highlights}>
          {highlights.map((highlight) =>
            highlight.count && canOpenTasks ? (
              <button key={highlight.text} type="button" className={`${styles.highlight} ${styles[`highlight_${highlight.tone}`]} ${styles.highlightLink}`} onClick={() => onOpenTasks(highlight.count)}>
                {highlight.text}
              </button>
            ) : (
              <span key={highlight.text} className={`${styles.highlight} ${styles[`highlight_${highlight.tone}`]}`}>
                {highlight.text}
              </span>
            )
          )}
        </div>
      )}

      {performance && <PerformanceSection state={performance} showHours={showHours} canOpenTasks={canOpenTasks} onOpenTasks={onOpenTasks} />}

      {budget && <BudgetSection state={budget} showHours={showHours} />}

      {showHours && period && (
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h4>Utilization</h4>
            <span className={styles.sectionHint}>{formatDateRange(period.startDate, period.endDate)}</span>
          </div>

          <div className={styles.utilizationValue}>
            <span className={`${styles.utilizationPercent} ${isOver ? styles.over : ""}`}>{Math.round(utilization)}%</span>
            <span className={styles.utilizationCaption}>
              {formatHours(period.allocatedHours)} allocated of {formatHours(period.assignedHours)} assigned
            </span>
          </div>

          <div className={styles.barTrack}>
            <div className={`${styles.barFill} ${isOver ? styles.barFillOver : ""}`} style={{ width: `${Math.min(utilization, 100)}%` }} />
          </div>

          <div className={styles.keyValues}>
            <div>
              <span>Capacity</span>
              <strong>{formatHours(period.capacityHours)}</strong>
              <em>{formatHours(maxWeeklyHours)} / week</em>
            </div>
            <div>
              <span>Assigned to projects</span>
              <strong>{formatHours(period.assignedWeeklyHours)}</strong>
              <em>per week</em>
            </div>
          </div>
        </section>
      )}

      {counts ? (
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h4>Tasks</h4>
            <span className={styles.sectionHint}>{formatDateRange(period.startDate, period.endDate)}</span>
          </div>
          <div className={styles.statGrid}>
            {STAT_TILES.map(({ key, label, icon }) => (
              <button key={key} type="button" className={`${styles.statTile} ${styles[`tone_${key}`]}`} onClick={() => onOpenTasks(key)}>
                <span className={styles.statTileLabel}>
                  {icon()}
                  {label}
                </span>
                <strong>{counts[key]}</strong>
              </button>
            ))}
          </div>
        </section>
      ) : (
        <p className={styles.note}>Task and allocation figures are not available with your permissions.</p>
      )}

      {showHours && period && period.byProject.length > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h4>Allocation by project</h4>
            <span className={styles.sectionHint}>{formatDateRange(period.startDate, period.endDate)}</span>
          </div>
          <AllocationDonut byProject={period.byProject} canOpenTasks={canOpenTasks} onOpenProject={(projectId) => onOpenTasks("active", projectId)} />
        </section>
      )}

      {isPartialView && <p className={styles.note}>Figures are based on the projects you can access.</p>}
    </div>
  );
}
